/**
 * SEO and metadata utilities for VegaDigital marketing website
 * Implements comprehensive SEO best practices for improved search visibility
 */

import { useEffect } from 'react';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'service';
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  noindex?: boolean;
}

// Default SEO configuration
const defaultSEO: Required<Omit<SEOProps, 'noindex'>> = {
  title: 'VegaDigital Solutions - Custom Software Development & Digital Solutions',
  description: 'Transform your business with expert software development, web applications, and digital solutions. Spec-driven development with transparent pricing and agile methodology.',
  keywords: [
    'software development',
    'web application development',
    'custom software',
    'digital solutions',
    'react development',
    'laravel development',
    'api development',
    'mobile app development',
    'database design',
    'devops services',
    'agile development',
    'spec-driven development',
  ],
  image: 'https://vegadigital.com/images/og-image.jpg',
  url: 'https://vegadigital.com',
  type: 'website',
  author: 'VegaDigital Solutions',
  publishedDate: '2025-01-01',
  modifiedDate: new Date().toISOString().split('T')[0],
};

/**
 * Utility function to set document head meta tags
 */
const setMetaTag = (name: string, content: string, isProperty = false): void => {
  const attribute = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.setAttribute('content', content);
};

/**
 * Utility function to set structured data script
 */
const setStructuredData = (id: string, data: object): void => {
  let script = document.getElementById(id) as HTMLScriptElement;
  
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  
  script.textContent = JSON.stringify(data);
};

/**
 * SEO Head component with comprehensive meta tags
 */
export const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  author,
  publishedDate,
  modifiedDate,
  noindex = false,
}) => {
  useEffect(() => {
    const seo = {
      title: title ? `${title} | VegaDigital Solutions` : defaultSEO.title,
      description: description || defaultSEO.description,
      keywords: Array.from(new Set([...keywords, ...defaultSEO.keywords])),
      image: image || defaultSEO.image,
      url: url || defaultSEO.url,
      type,
      author: author || defaultSEO.author,
      publishedDate: publishedDate || defaultSEO.publishedDate,
      modifiedDate: modifiedDate || defaultSEO.modifiedDate,
    };

    // Set document title
    document.title = seo.title;

    // Basic meta tags
    setMetaTag('description', seo.description);
    setMetaTag('keywords', seo.keywords.join(', '));
    setMetaTag('author', seo.author);

    // Robots meta
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      setMetaTag('robots', 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    }

    // Open Graph meta tags
    setMetaTag('og:title', seo.title, true);
    setMetaTag('og:description', seo.description, true);
    setMetaTag('og:image', seo.image, true);
    setMetaTag('og:url', seo.url, true);
    setMetaTag('og:type', seo.type, true);
    setMetaTag('og:site_name', 'VegaDigital Solutions', true);
    setMetaTag('og:locale', 'en_US', true);

    // Twitter Card meta tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', seo.title);
    setMetaTag('twitter:description', seo.description);
    setMetaTag('twitter:image', seo.image);
    setMetaTag('twitter:site', '@vegadigital');
    setMetaTag('twitter:creator', '@vegadigital');

    // Article meta tags (for blog posts/articles)
    if (type === 'article') {
      setMetaTag('article:author', seo.author, true);
      setMetaTag('article:published_time', seo.publishedDate, true);
      setMetaTag('article:modified_time', seo.modifiedDate, true);
      setMetaTag('article:section', 'Technology', true);
      
      // Add first 5 keywords as article tags
      seo.keywords.slice(0, 5).forEach((keyword, index) => {
        setMetaTag(`article:tag:${index}`, keyword, true);
      });
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = seo.url;

    // Additional SEO enhancements
    setMetaTag('format-detection', 'telephone=no');
    setMetaTag('theme-color', '#2563eb');
    setMetaTag('msapplication-TileColor', '#2563eb');

    // Structured Data - Organization
    setStructuredData('org-schema', {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VegaDigital Solutions",
      "url": "https://vegadigital.com",
      "logo": "https://vegadigital.com/images/logo.png",
      "description": "Expert software development and digital solutions company specializing in custom web applications, API development, and digital transformation.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Francisco",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "contact@vegadigitalsolutions.com"
      },
      "sameAs": [
        "https://twitter.com/vegadigital",
        "https://linkedin.com/company/vegadigital",
        "https://github.com/vegadigital"
      ]
    });

    // Structured Data - Local Business
    setStructuredData('business-schema', {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "VegaDigital Solutions",
      "description": "Custom software development and digital solutions",
      "url": "https://vegadigitalsolutions.com",
      "email": "contact@vegadigitalsolutions.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Francisco",
        "addressRegion": "CA",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "37.7749",
        "longitude": "-122.4194"
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "priceRange": "$$",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "50"
      }
    });
  }, [title, description, keywords, image, url, type, author, publishedDate, modifiedDate, noindex]);

  return null;
};

/**
 * Page-specific SEO configurations
 */
export const pageSEO = {
  home: {
    title: 'Custom Software Development & Digital Solutions',
    description: 'Transform your business with expert software development, web applications, and digital solutions. Spec-driven development with transparent pricing and agile methodology.',
    keywords: ['custom software development', 'web application development', 'digital transformation', 'software consulting'],
    url: 'https://vegadigital.com',
  },
  
  services: {
    title: 'Our Services - Web Development, API & Mobile Apps',
    description: 'Comprehensive software development services including web applications, API development, mobile apps, database design, and DevOps. Expert React and Laravel development.',
    keywords: ['web development services', 'api development', 'mobile app development', 'react development', 'laravel development'],
    url: 'https://vegadigital.com/services',
  },
  
  about: {
    title: 'About Us - Expert Software Development Team',
    description: 'Meet the VegaDigital team of expert developers, designers, and project managers. Learn about our mission, values, and commitment to delivering exceptional software solutions.',
    keywords: ['software development team', 'about vegadigital', 'development expertise', 'company mission'],
    url: 'https://vegadigital.com/about',
  },
  
  contact: {
    title: 'Contact Us - Get Your Free Consultation',
    description: 'Ready to start your project? Contact VegaDigital for a free consultation. 24-hour response guarantee. Let\'s discuss your software development needs.',
    keywords: ['contact software developers', 'free consultation', 'project quote', 'software development inquiry'],
    url: 'https://vegadigital.com/contact',
  },
};

/**
 * Generate breadcrumb structured data
 */
export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  };
};

/**
 * Generate FAQ structured data
 */
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };
};

/**
 * Generate service structured data
 */
export const generateServiceSchema = (service: {
  name: string;
  description: string;
  provider: string;
  areaServed: string;
  serviceType: string;
}) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "provider": {
      "@type": "Organization",
      "name": service.provider,
    },
    "areaServed": {
      "@type": "Place",
      "name": service.areaServed,
    },
    "serviceType": service.serviceType,
  };
};

export default SEOHead;